"use client";

import { Activity, Brain, Compass, Flame, ChartLine as LineChart, Radar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ShippingPolicyGrid() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "Global Standard Logistics",
    description: "Our foundational delivery service ensures your curated selections arrive with meticulous care within 3-5 business days across the continent.",
    Icon: LineChart
  }, {
    number: "02",
    title: "Priority Express Atelier",
    description: "For those who seek immediate elegance, our priority service guarantees arrival within 1-2 business days for all in-stock masterpieces.",
    Icon: Brain
  }, {
    number: "03",
    title: "International Concierge",
    description: "Bridging the distance between our atelier and your doorstep, we offer seamless global transit to over 50 premier international destinations.",
    Icon: Radar
  }, {
    number: "04",
    title: "Real-Time Transparency",
    description: "Maintain complete oversight of your journey with sophisticated, real-time tracking updates delivered directly to your private correspondence.",
    Icon: Compass
  }, {
    number: "05",
    title: "White-Glove Returns",
    description: "We honor the perfect fit. Experience our refined 30-day return process for all unworn items, ensuring a risk-free acquisition every time.",
    Icon: Activity
  }, {
    number: "06",
    title: "Complimentary Transit",
    description: "As a gesture of our commitment to excellence, we provide complimentary standard shipping on all orders exceeding the $150 threshold.",
    Icon: Flame
  }];
  return <section data-section-id="4553" id="shippingpolicygrid" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-default text-xs tracking-widest uppercase text-primary mb-6">
              03 / Shipping & Returns
            </p>
            <h2 className="font-default font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Shipping & Returns Policy
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground/90 max-w-2xl">
              At Lush Fashion, we believe the journey is as important as the destination. Our shipping and return protocols are built on a foundation of absolute transparency, meticulous care, and an unwavering commitment to service excellence.
            </p>
          </div>
        </div>

        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((cap, i) => {
          const Icon = cap.Icon;
          return <div data-index={i} key={cap.number} className="group relative bg-background border border-border/40 hover:border-primary/40 hover:bg-secondary/50 hover:scale-[1.02] transition-all duration-500 ease-out p-10 sm:p-12 flex flex-col" style={{
            aspectRatio: "4 / 5",
            minHeight: "420px"
          }}>
                
                <span className="font-default text-xs tracking-widest text-primary">
                  {cap.number}
                </span>

                
                <div className="relative flex items-center justify-start mt-10 mb-10">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-primary/15 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon strokeWidth={1} className="relative size-10 text-foreground/90 group-hover:text-primary transition-colors duration-500" />
                </div>

                
                <h3 className="font-default font-semibold text-2xl leading-snug text-foreground mb-4">
                  {cap.title}
                </h3>

                
                <p className="font-default text-[15px] leading-relaxed text-muted-foreground/80 mb-8">
                  {cap.description}
                </p>

                
                <div className="mt-auto">
                  <Link to="/ShopPage" className="inline-flex items-center gap-2 font-default text-xs tracking-widest uppercase text-foreground/80 hover:text-primary transition-colors duration-300">
                    <span className="relative">
                      <span className="bg-left-bottom bg-gradient-to-r from-primary to-primary bg-[length:0%_1px] bg-no-repeat group-hover:bg-[length:100%_1px] transition-[background-size] duration-500 ease-out pb-0.5">
                        Shop Now
                      </span>
                    </span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>;
        })}
        </div>

        
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-border">
          <p className="font-default text-sm text-muted-foreground/80 max-w-xl">
            Need further assistance? Our dedicated customer care team is available to ensure your experience remains seamless and sophisticated.
          </p>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-default text-xs tracking-widest uppercase rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
            <Link to="/CustomerCarePage">Contact Support</Link>
          </Button>
        </div>
      </div>
    </section>;
}